import type { Route } from 'next';
import { NOTIFICATION_CATEGORIES } from '@unreserved/constants';
import type { NotificationItemDto } from '@unreserved/sdk';
import { generateToken } from '@/lib/firebase';
import { getAdminCustomersApi } from '@/lib/utils/admin-sdk';
import { DEVICE_ID_KEY } from '@unreserved/constants/notifications';

export const CATEGORY_TO_TYPE: Record<
  (typeof NOTIFICATION_CATEGORIES)[number],
  string | null
> = {
  All: null,
  Offers: 'offer',
  Auctions: 'auction',
  Enquiries: 'enquiry',
};

export const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

type NotificationRouteData = {
  notification_type: string;
  listing_id?: string | null;
  enquiry_id?: string | null;
  deep_link?: string | null;
};

export function resolveNotificationRoute(data: NotificationRouteData): Route {
  switch (data.notification_type) {
    case 'enquiry':
      return (
        data.enquiry_id
          ? `/enquiries-management/?id=${data.enquiry_id}`
          : '/dashboard'
      ) as Route;
    case 'offer':
    case 'auction':
      return (
        data.listing_id
          ? `/customer-management/properties/${data.listing_id}`
          : '/dashboard'
      ) as Route;
    case 'custom':
      return (data.deep_link ?? '/dashboard') as Route;
    default:
      return '/dashboard' as Route;
  }
}

const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem(DEVICE_ID_KEY);
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
  }
  return deviceId;
};

export const registerFcmToken = async () => {
  if (typeof window === 'undefined') return;

  try {
    const token = await generateToken();
    if (!token) return;
    const adminCustomersApi = getAdminCustomersApi();

    await adminCustomersApi.userControllerRegisterFcmTokenV1({
      registerFcmTokenDto: {
        deviceId: getOrCreateDeviceId(),
        deviceName: navigator.userAgent,
        pushNotificationToken: token,
        platform: 'web',
      },
    });
  } catch (error) {
    console.error('Failed to register FCM token:', error);
  }
};

export function handleNotificationClick(
  item: NotificationItemDto,
  router: { push: (path: Route) => void },
) {
  const route = resolveNotificationRoute({
    notification_type: item.notification_type,
    listing_id: item.listing_id ?? undefined,
    enquiry_id: item.enquiry_id ?? undefined,
    deep_link: item.deep_link ?? undefined,
  });

  router.push(route);
}
